import { ValidatorFunction } from 'router/types';

import { store } from 'store/configureStore';
import { selectIsNavReady } from 'store/features/selectors';
import { selectIsPaid } from 'store/user/selectors';

import { canAccess } from './canAccess';
import { findNavItemByRoutePattern } from './config';
import { AccessRule, NavAccess } from './types';

/**
 * Sync store read for route validators (mirrors `getAuthStoreData`).
 * Shares the `selectIsPaid` predicate with `useNavAccess` — read off the store, no hooks.
 */
export const getNavAccess = (): NavAccess => {
    const state = store.getState();

    return {
        isPaid: selectIsPaid(state),
        flags: state.features?.flags ?? {},
        features: state.features?.features ?? {},
    };
};

/**
 * Validator factory for `ProtectedRoute` — gates a route by an access rule.
 *
 * Feature/flag gates resolve asynchronously: `useBootstrapNavigation` only
 * fetches `/features` + Eppo flags after auth completes. But validators run
 * synchronously at route-match time, before that fetch lands — so on a hard
 * refresh / deep link to a gated route, `getNavAccess()` reads empty
 * features/flags and a naive `canAccess` would deny access and bounce the user
 * to MAIN before the data ever loads.
 *
 * So while nav is not ready we treat feature/flag gates as **pending, not
 * denied**: evaluate only the synchronous `account` gate (`is_paid` settles
 * with the auth gate) and let the route through, deferring feature/flag
 * enforcement to `useEnforceAccess`, which waits for `isNavReady` and kicks the
 * user off if the page is truly inaccessible. On warm client-side navigations
 * nav is already ready, so the full rule is enforced here as before.
 *
 * Note: this defers — it does not skip — enforcement, so a feature/flag-gated
 * route must have a matching `NAV_ITEMS` entry for `useEnforceAccess` to act on.
 */
export const canAccessRoute =
    (access: AccessRule): ValidatorFunction =>
    () => {
        const navReadyPending = !selectIsNavReady(store.getState()) && Boolean(access.flag || access.feature);
        const effectiveAccess = navReadyPending ? { account: access.account } : access;

        return canAccess(effectiveAccess, getNavAccess());
    };

/**
 * Validator for a route that is also a sidebar entry: gates on that entry's
 * `NAV_ITEMS` access rule instead of a hand-written copy, so the route guard
 * and the registry share one definition and cannot drift. Inherits the cold-start
 * deferral of `canAccessRoute` (feature/flag gates wait for `useEnforceAccess`).
 *
 * Falls back to a paid-only gate if `path` has no registry entry — these are all
 * paid routes, so that stays safe — but every current caller passes a registered
 * path, keeping the registry authoritative.
 */
export const canAccessNavRoute = (path: string): ValidatorFunction => {
    const item = findNavItemByRoutePattern(path);

    return canAccessRoute(item?.access ?? { account: 'paid' });
};
